import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useMeQuery } from '../hooks/useMe';
import { useAuthStore } from '../stores/authStore';
import { LoadingSkeleton } from './LoadingSkeleton';

// Routes that don't require authentication. The user MAY visit them while
// signed in, but the guard won't redirect them away.
const PUBLIC_PATHS = new Set<string>(['/login']);

export function AuthGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const isPublic = PUBLIC_PATHS.has(location.pathname);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Bootstrap: on first mount (and on every reload) we don't have an access
  // token in memory yet. Hit /me — apiClient's 401 → /auth/refresh interceptor
  // will silently mint a new token from the httpOnly refresh cookie if the
  // user is still signed in. If refresh fails, /me's request rejects.
  const me = useMeQuery(!isPublic);

  // If signed in and trying to view /login, send them to /projects.
  useEffect(() => {
    if (isPublic && isAuthenticated && location.pathname === '/login') {
      // Handled by the Navigate below; the effect just ensures we don't keep
      // the URL parked at /login.
    }
  }, [isPublic, isAuthenticated, location.pathname]);

  if (isPublic) {
    if (isAuthenticated && location.pathname === '/login') {
      return <Navigate to="/projects" replace />;
    }
    return <>{children}</>;
  }

  if (me.isLoading) {
    return (
      <div className="ta-route-fallback">
        <LoadingSkeleton variant="text" lines={6} />
      </div>
    );
  }

  if (me.isError || !me.data) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
